import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { useTabLeader } from './useTabLeader';
import { useBackgroundPause } from './useBackgroundPause';
import { differenceInMinutes, isPast, parseISO } from 'date-fns';

interface ScheduledCall {
  id: string;
  title: string;
  scheduled_for: string;
  reminder_time: string | null;
  to_e164: string;
  contact_id: string;
  lead_id: string | null;
  call_purpose: string | null;
}

export function useCallNotifications() {
  const { user } = useAuth();
  const [notifiedCalls, setNotifiedCalls] = useState<Set<string>>(new Set());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Leader election - only leader tab processes notifications
  const { isLeader } = useTabLeader('call-notifications-leader');
  // Background detection - pause when tab is hidden
  const { isBackground } = useBackgroundPause();

  useEffect(() => {
    if (!user) return;

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const checkUpcomingCalls = async () => {
      // Skip if not leader or tab is in background
      if (!isLeader || isBackground) {
        return;
      }

      try {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (!appUser) return;

        // Get scheduled calls for this user that haven't happened yet
        const { data: calls, error } = await supabase
          .from('calls')
          .select('id, title, scheduled_for, reminder_time, to_e164, contact_id, lead_id, call_purpose')
          .eq('handled_by_app_user_id', appUser.id)
          .eq('status', 'scheduled')
          .eq('channel', 'voice')
          .not('scheduled_for', 'is', null)
          .gte('scheduled_for', new Date().toISOString())
          .order('scheduled_for', { ascending: true });

        if (error) {
          console.error('Error fetching scheduled calls:', error);
          return;
        }

        const now = new Date();

        calls?.forEach((call: ScheduledCall) => {
          // Skip if already notified
          if (notifiedCalls.has(call.id)) return;

          const scheduledTime = parseISO(call.scheduled_for);
          const minutesUntilCall = differenceInMinutes(scheduledTime, now);

          // Default notification time is 15 minutes before
          let notificationMinutes = 15;

          if (call.reminder_time) {
            try {
              const reminderTime = parseISO(call.reminder_time);
              notificationMinutes = differenceInMinutes(scheduledTime, reminderTime);
            } catch {
              // If parsing fails, use default
              notificationMinutes = 15;
            }
          }

          // Check if we should notify (within notification window, but not past the call time)
          if (minutesUntilCall <= notificationMinutes && minutesUntilCall > 0 && !isPast(scheduledTime)) {
            // Show toast notification only on desktop
            const isMobile = window.innerWidth < 768;
            if (!isMobile) {
              toast.info(`📞 Ligação em breve: ${call.title || 'Sem título'}`, {
                description: `Agendada em ${minutesUntilCall} minuto${minutesUntilCall !== 1 ? 's' : ''}${call.call_purpose ? ` - ${call.call_purpose}` : ''}`,
                duration: 10000,
                action: {
                  label: 'Ver',
                  onClick: () => {
                    window.location.href = '/calls';
                  },
                },
              });
            }

            // Show browser notification if permission granted
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Ligação em breve', {
                body: `${call.title || 'Ligação'} em ${minutesUntilCall} minuto${minutesUntilCall !== 1 ? 's' : ''}`,
                icon: '/favicon.ico',
                tag: call.id,
              });
            }

            // Mark as notified
            setNotifiedCalls((prev) => new Set(prev).add(call.id));
          }
        });
      } catch (error) {
        console.error('Error checking upcoming calls:', error);
      }
    };

    // Check immediately (only if leader and visible)
    if (isLeader && !isBackground) {
      checkUpcomingCalls();
    }

    // Then check every minute
    checkIntervalRef.current = setInterval(checkUpcomingCalls, 60000);

    // Set up real-time subscription for new scheduled calls - only leader subscribes
    let channel: ReturnType<typeof supabase.channel> | null = null;

    if (isLeader) {
      channel = supabase
        .channel('call-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'calls',
            filter: `status=eq.scheduled`,
          },
          () => {
            console.log('New scheduled call detected, checking notifications...');
            checkUpcomingCalls();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'calls',
          },
          (payload) => {
            // If a call status changed from scheduled to something else, remove from notified set
            if (payload.new.status !== 'scheduled' && notifiedCalls.has(payload.new.id)) {
              setNotifiedCalls((prev) => {
                const newSet = new Set(prev);
                newSet.delete(payload.new.id);
                return newSet;
              });
            }
          }
        )
        .subscribe();
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, notifiedCalls, isLeader, isBackground]);

  return null;
}
